import { DashboardCell, DashboardLayout, DashboardPage, DashboardRow } from '../types/gotypes'

/**
 * Duplicating a widget, row or tab in the dashboard editor.
 *
 * This replaces the typed clipboard of #967. That model asked the user to
 * reason about rows, cells, capacity ceilings and paste types, and answered a
 * mistimed click with a structural error ("This row is full…"). Matt's
 * usability testing rejected it, and the layout working session settled on the
 * simpler contract: **copy duplicates, in place**. The copy goes into the next
 * slot — same row if there is room, a new row directly below if there is not —
 * and the layout adapts. There is nothing to aim, so there is nothing to
 * block, so there are no blocked-paste messages left to write.
 *
 * What survives from the old module is the part that was never about
 * clipboards: a duplicate still needs its own multi-table definition, and
 * still must not inherit the original's sign-off text.
 */

/**
 * Deep-copy a node before it is duplicated.
 *
 * Everything downstream (provenance reset, id remapping) is spread-based, so
 * without this the copy would keep sharing `table_graph_data`, `text_data`,
 * `display_props` and nested layout objects with the live config — editing the
 * copy could then mutate the source in place.
 */
export function snapshotForDuplicate<T>(node: T): T {
  return structuredClone(node)
}

/**
 * Recursively strip provenance the copy must never inherit.
 *
 * Today that is `edited_report` — the human-edited/sign-off text on a Gen AI
 * Insights card (#963). A duplicated card arrives unsigned.
 *
 * `multi_table_id` is deliberately NOT cleared here. The duplicate flow forks
 * the multi-table row instead (see `collectMultiTableRefs` /
 * `remapMultiTableIds` and `forkMultiTables` in DashboardEditor): the copy gets
 * its own multi-table definition pre-pointed at the same tables, so a
 * duplicated column of analysis cards works immediately and the two cards can
 * diverge independently. Because the new id is fresh, everything keyed off
 * multi_table_id — edit history, analyses, subscribers — starts empty for the
 * copy.
 *
 * Recurses into nested `layout_data` and into a report pillar's entries so a
 * duplicated column/row/tab is fully clean.
 */
export function resetCellProvenance(cell: DashboardCell): DashboardCell {
  const next: DashboardCell = { ...cell }
  if (next.multi_table_analysis_data) {
    next.multi_table_analysis_data = {
      ...next.multi_table_analysis_data,
      edited_report: undefined,
    }
  }
  if (next.layout_data) {
    next.layout_data = resetLayoutProvenance(next.layout_data)
  }
  // A report pillar's entries are ordinary cells and clean the same way (#1080).
  if (next.report_pillar_data) {
    next.report_pillar_data = {
      ...next.report_pillar_data,
      entries: next.report_pillar_data.entries.map(entry => ({
        ...entry,
        cell: resetCellProvenance(entry.cell),
      })),
    }
  }
  return next
}

function resetLayoutProvenance(layout: DashboardLayout): DashboardLayout {
  return {
    ...layout,
    rows: layout.rows.map(row => ({
      ...row,
      cells: row.cells.map(resetCellProvenance),
    })),
  }
}

/** A multi-table definition referenced by a duplicated node, with the job it reads. */
export interface MultiTableRef {
  id: string
  job?: string
}

/** Every distinct multi-table referenced by these cells, recursing into nested layouts. */
export function collectMultiTableRefs(cells: DashboardCell[]): MultiTableRef[] {
  const refs: MultiTableRef[] = []
  const seen = new Set<string>()
  const walk = (list: DashboardCell[]) => {
    for (const cell of list) {
      const data = cell.multi_table_analysis_data
      if (data?.multi_table_id && !seen.has(data.multi_table_id)) {
        seen.add(data.multi_table_id)
        refs.push({ id: data.multi_table_id, job: data.job })
      }
      if (cell.layout_data) {
        for (const row of cell.layout_data.rows) walk(row.cells)
      }
      if (cell.report_pillar_data) {
        walk(cell.report_pillar_data.entries.map(entry => entry.cell))
      }
    }
  }
  walk(cells)
  return refs
}

/** Same, for a list of rows (a duplicated row, or every row of a duplicated tab). */
export function collectMultiTableRefsInRows(rows: DashboardRow[]): MultiTableRef[] {
  return collectMultiTableRefs(rows.flatMap(row => row.cells))
}

/**
 * Re-point every multi_table_analysis cell at its forked multi-table.
 * Ids missing from `mapping` are left as-is (the fork failed; the copy keeps
 * pointing at the original rather than losing its tables entirely).
 */
export function remapMultiTableIds(cells: DashboardCell[], mapping: Record<string, string>): DashboardCell[] {
  return cells.map(cell => {
    let next = cell
    const currentId = cell.multi_table_analysis_data?.multi_table_id
    if (currentId && mapping[currentId]) {
      next = {
        ...next,
        multi_table_analysis_data: { ...next.multi_table_analysis_data!, multi_table_id: mapping[currentId] },
      }
    }
    if (next.layout_data) {
      next = {
        ...next,
        layout_data: {
          ...next.layout_data,
          rows: remapMultiTableIdsInRows(next.layout_data.rows, mapping),
        },
      }
    }
    if (next.report_pillar_data) {
      const remapped = remapMultiTableIds(next.report_pillar_data.entries.map(entry => entry.cell), mapping)
      next = {
        ...next,
        report_pillar_data: {
          ...next.report_pillar_data,
          entries: next.report_pillar_data.entries.map((entry, i) => ({ ...entry, cell: remapped[i] })),
        },
      }
    }
    return next
  })
}

/** Same, for a list of rows. */
export function remapMultiTableIdsInRows(rows: DashboardRow[], mapping: Record<string, string>): DashboardRow[] {
  return rows.map(row => ({ ...row, cells: remapMultiTableIds(row.cells, mapping) }))
}

/** Clone a cell for duplication: fresh identity, provenance reset. */
export function cloneCellForDuplicate(cell: DashboardCell): DashboardCell {
  return resetCellProvenance(snapshotForDuplicate(cell))
}

/** Clone a row for duplication: every cell inside is reset too. */
export function cloneRowForDuplicate(row: DashboardRow): DashboardRow {
  const copy = snapshotForDuplicate(row)
  return { ...copy, cells: copy.cells.map(resetCellProvenance) }
}

/** Clone a tab (page) for duplication: fresh page id, "<name> copy" name, provenance reset throughout. */
export function clonePageForDuplicate(page: DashboardPage): DashboardPage {
  const copy = snapshotForDuplicate(page)
  return {
    id: crypto.randomUUID(),
    name: `${copy.name} copy`,
    layout: resetLayoutProvenance(copy.layout),
    // #1182: a duplicated page inherits the source page's theme reference.
    ...(copy.brand_palette_key ? { brand_palette_key: copy.brand_palette_key } : {}),
  }
}

/**
 * Drop a cell's explicit width so the destination row redistributes evenly.
 * Without this a cell duplicated out of a 70/30 row keeps `width: 70` and skews
 * (or blanks out) the row it lands in.
 */
export function stripCellWidth(cell: DashboardCell): DashboardCell {
  if (!cell.display_props) return cell
  return { ...cell, display_props: { ...cell.display_props, width: undefined } }
}

/**
 * True when a top-level cell represents a "column" — a nested layout, or a
 * report pillar (#1080), which is a column of cards by another name — rather
 * than a plain widget. In the rows-only layout model a column is just a wide
 * widget: it drags, resizes and duplicates like any other. This survives only
 * so the controls acting on one can NAME it correctly (#1075).
 */
export function isColumnCell(cell: DashboardCell): boolean {
  return cell.widget_type === 'layout' || cell.widget_type === 'report_pillar'
}
